import React from 'react';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactCountryFlag from 'react-country-flag';
import { updateUserLocale } from '../../api/users';
import { setUserLocale } from '../../store/slices/authSlice';

export const LANGUAGES = [
  { key: 'en', apiCode: 'en_US', label: 'English', countryCode: 'GB' },
  { key: 'fr', apiCode: 'fr_FR', label: 'Français', countryCode: 'FR' },
  { key: 'es', apiCode: 'es_ES', label: 'Español', countryCode: 'ES' },
  { key: 'de', apiCode: 'de_DE', label: 'Deutsch', countryCode: 'DE' },
  { key: 'nl', apiCode: 'nl_BE', label: 'Nederlands', countryCode: 'BE', dualCodes: ['BE', 'NL'] },
  // { key: 'zh', label: '中文', countryCode: 'CN' },
  // { key: 'ja', label: '日本語', countryCode: 'JP' },
];

// Map API locale code (e.g. 'de_DE') back to short i18n key (e.g. 'de')
export const apiLocaleToKey = (apiCode) => {
  const lang = LANGUAGES.find((l) => l.apiCode === apiCode);
  return lang?.key || 'en';
};

// Change locale everywhere: i18n + localStorage + backend (if user is logged in)
// After a successful backend update, reload the page so any cached translated
// content (categories, products, product_attributes.*) is refetched in the
// new locale — per the i18n integration guide §5.2.
export const changeAppLocale = async (
  key,
  { i18n, dispatch, isLoggedIn = true, reloadAfter = true } = {},
) => {
  if (i18n) i18n.changeLanguage(key);
  localStorage.setItem('app_language', key);

  const lang = LANGUAGES.find((l) => l.key === key);
  if (!isLoggedIn || !lang?.apiCode) return;

  try {
    await updateUserLocale(lang.apiCode);
    if (dispatch) dispatch(setUserLocale(lang.apiCode));
  } catch (e) {
    console.error('Failed to sync locale with backend:', e?.response?.status, e?.response?.data?.message || e.message);
  }
  if (reloadAfter) {
    window.location.reload();
  }
};

const FlagIcon = ({ countryCode, size = 20 }) => (
  <ReactCountryFlag
    countryCode={countryCode}
    svg
    style={{ width: size, height: size * 0.75, borderRadius: 2, objectFit: 'cover', border: '1px solid #eee' }}
  />
);

const DualFlag = ({ codes, size = 20 }) => (
  <span className="d-flex" style={{ gap: 2 }}>
    {codes.map(code => (
      <ReactCountryFlag
        key={code}
        countryCode={code}
        svg
        style={{ width: size * 0.6, height: size * 0.45, borderRadius: 1, objectFit: 'cover', border: '1px solid #eee' }}
      />
    ))}
  </span>
);

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => Boolean(state.auth.token));

  const currentLang = LANGUAGES.find(l => l.key === i18n.language) || LANGUAGES[0];

  const handleChange = (key) => {
    changeAppLocale(key, { i18n, dispatch, isLoggedIn });
  };

  const renderFlag = (lang) => lang.dualCodes
    ? <DualFlag codes={lang.dualCodes} />
    : <FlagIcon countryCode={lang.countryCode} />;

  const items = LANGUAGES.map(lang => ({
    key: lang.key,
    label: (
      <span className="d-flex align-items-center gap-8">
        {renderFlag(lang)}
        <span>{lang.label}</span>
      </span>
    ),
    onClick: () => handleChange(lang.key),
  }));

  return (
    <Dropdown menu={{ items, selectedKeys: [currentLang.key] }} trigger={['click']} placement="bottomRight">
      <div className="language-switcher cursor-pointer d-flex align-items-center gap-8">
        {renderFlag(currentLang)}
        {!compact && <span className="language-switcher-label">{currentLang.label}</span>}
        <img src="/img/icons/content-right-section/chevron-down.svg" alt="" className="language-switcher-arrow" />
      </div>
    </Dropdown>
  );
};

export default LanguageSwitcher;
